import { InsightGetSymbolsPerTypeResponse } from '@orbis-systems/orbis-api-clients/insight';
import { ClientResponse } from '@orbis-systems/orbis-api-clients/orbis';
import { flatMap, isArray } from 'lodash-es';
import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import { API_DATE, API_DATE_WITH_TIME_AND_TIMEZONE } from '../constants/datePatterns';
import {
    AcatAccountContraType,
    AcatTransferType,
    AccountCurrencyApproach,
    BondQuantityType,
    CorporateActionsType,
    Currency,
    IraContributionType,
    IraDistributionReasonType,
    IraDistributionTaxType,
    OrderFilterType,
    PriceType,
    WireDisbursementType,
} from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    Acat,
    Apis,
    HoldingInfo,
    MiddlewareConfig,
    Order,
    OrderWithExtraInfo,
    TiprankOverviewWithExtraInfo,
} from '../types';
import {
    Account,
    AccountBuyingPower,
    AccountCharge,
    AccountChargesApply,
    AccountChargesCancel,
    AccountCombinedBalance,
    AccountHistory,
    AccountHistoryWithExtraInfo,
    AccountLight,
    AccountWithExtraInfo,
    AccountWithPositionInfo,
    AxisBankLink,
    AxisBankLinkFormValues,
    BalanceHistory,
    BankLink,
    CancelTransaction,
    CreateAchBankAccountFormValues,
    CreateIfscBankAccountFormValues,
    DepositWithdraw,
    MicroDepositFormValues,
    RequestCheckWithdrawalFormValues,
    Transfer,
    WireTransferFormValues,
} from '../types/Account.type';
import { CorporateAction, CorporateActionWithExtraInfo } from '../types/CorporateAction.type';
import { AccountDriftReportItem } from '../types/DriftReport.type';
import { Group } from '../types/Group.type';
import { Model, ModelWithExtraInfo } from '../types/Model.type';
import { Portfolio, PortfolioWithExtraInfo, Position } from '../types/Portfolio.type';
import { formatDate } from '../utils';
import {
    formatAccountHistory,
    formatCorporateActions,
    formatDriftReport,
    formatHoldingInfo,
    formatOrders,
    formatPortfolio,
    formatPortfolioPositions,
} from '../utils/dataUtils';
import { sumBy } from '../utils/numberUtils';
import type { BranchApi } from './BranchApi';
import type { GroupApi } from './GroupApi';
import type { ModelApi } from './ModelApi';
import type { QuoteApi } from './QuoteApi';
import type { TipranksApi } from './TipranksApi';
import type { UserApi } from './UserApi';

@injectable()
export class AccountApi extends CachedResponseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.BranchApi) protected readonly branchApi: BranchApi,
        @inject(ApiInjectToken.ModelApi) protected readonly modelApi: ModelApi,
        @inject(ApiInjectToken.GroupApi) protected readonly groupApi: GroupApi,
        @inject(ApiInjectToken.QuoteApi) protected readonly quoteApi: QuoteApi,
        @inject(ApiInjectToken.UserApi) protected readonly userApi: UserApi,
        @inject(ApiInjectToken.TipranksApi) protected readonly tipranksApi: TipranksApi,
    ) {
        super(apis, config, cache);
    }

    async searchAccounts(criteria: string | number, limit?: number): Promise<AccountLight[]> {
        const response = await this.apis.orbis.user.accountSearch({
            criteria,
            limit,
        });
        return response.data;
    }

    async getAccounts(): Promise<Account[]> {
        const response = await this.userApi.getUserInfo();
        return response.accountList;
    }
    async getTopAccounts(limit?: number): Promise<AccountLight[]> {
        const response = await this.apis.orbis.user.top10AccountList({
            limit: limit || 10,
        });
        return response.data;
    }

    async getAccountInfo(accountId: number): Promise<Account | null> {
        const response = await this.apis.orbis.user.accountInformation({ accountId: String(accountId) });
        return response.data;
    }

    async getAccount(accountId: number, currency?: string): Promise<AccountWithExtraInfo | null> {
        const accountInfo = await this.getAccountInfo(accountId);

        const accountRtbs = this.config.apiPermissions.rtbs
            ? await this.apis.orbis.user.accountBalance({
                  // @ts-expect-error // FIXME: some params (accountId) don't exist, waiting for backend to update
                  accountId,
                  currency: currency ?? Currency.UsDollar,
              })
            : undefined;

        if (!accountInfo) {
            return null;
        }

        const {
            balance = 0,
            totalDividendAmount = 0,
            totalDividendYield = 0,
            sodCashBalance = 0,
            sodMarginBalance = 0,
            sodShortBalance = 0,
            cashOnAccount = 0,
            markToMarket = 0,
        } = accountRtbs?.data || {};

        return {
            ...accountInfo,
            balance,
            totalDividendAmount,
            totalDividendYield,
            sodCashBalance,
            sodMarginBalance,
            sodShortBalance,
            cashOnAccount,
            markToMarket,
        };
    }

    async getPortfolio({
        accountId,
        symbol,
        tipranks,
        currency,
        loadIndustries = true, // loadIndustries is true by default for OV
    }: {
        accountId: number;
        symbol?: string;
        tipranks?: boolean;
        currency?: string;
        loadIndustries?: boolean;
    }): Promise<PortfolioWithExtraInfo> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor
                ? this.branchApi.getAccount(accountId || 0)
                : this.getAccount(accountId || 0),
            this.apis.orbis.user.accountPortfolioFull({
                loadQuotes: true,
                // @ts-expect-error: // FIXME: missing query param. Waiting for backend update
                loadCost: true,
                loadIndustries,
                accountId,
                currency: currency ?? Currency.UsDollar,
                bondPriceType: PriceType.Percentage,
                bondQuantityType: BondQuantityType.Unit,
            }),
        ]);
        let tipranksData: TiprankOverviewWithExtraInfo[] = [];
        let groupedSymbolsByFundType: InsightGetSymbolsPerTypeResponse | undefined = undefined;

        const account = data[0];
        const portfolio = data[1].data as Portfolio;

        if (tipranks && !!portfolio.entries.length) {
            try {
                if (symbol) {
                    tipranksData = await this.tipranksApi.getTipranksAssetsOverview(
                        // Option symbols will need to be sent as their root symbol. rootSymbol will not exist on basic equity quotes
                        portfolio.entries
                            .map((entry: Position) => entry.quote.rootSymbol || entry.quote.symbol)
                            .filter((entry) => entry === symbol),
                    );
                } else {
                    tipranksData = await this.tipranksApi.getTipranksAssetsOverview(
                        portfolio.entries.map((entry: Position) => entry.quote.rootSymbol || entry.quote.symbol),
                    );
                }
            } catch {
                // no-op
            }
        }

        try {
            groupedSymbolsByFundType = await this.quoteApi.groupSymbolsByFundType(
                portfolio.entries.map((entry: Position) => entry.symbol),
            );
        } catch {
            // no-op
        }

        return formatPortfolio(portfolio, account ? [account] : [], [], account?.balance || 0, {
            tipranks: tipranksData,
            symbol,
            groupedSymbolsByFundType,
        });
    }

    async getOrders(
        accountId: number,
        filterType: OrderFilterType | OrderFilterType[] = OrderFilterType.Latest,
        symbol?: string,
        option?: boolean,
    ): Promise<OrderWithExtraInfo[]> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor
                ? this.apis.orbis.advisory.models.listing()
                : Promise.resolve({ data: [] }),
            this.config.apiPermissions.advisor ? this.branchApi.getInventoryAccounts() : Promise.resolve([]),
            this.config.apiPermissions.advisor ? this.getAccounts() : Promise.resolve([]),
            ...(isArray(filterType) ? filterType : [filterType]).map((filter) => {
                return this.apis.orbis.orders.listing(filter, {
                    accountId,
                    loadQuotes: true,
                    symbol,
                    // @ts-expect-error: // FIXME: missing query param {option}. Waiting for backend update
                    option: option,
                    bondPriceType: PriceType.Percentage,
                    bondQuantityType: BondQuantityType.Unit,
                });
            }),
        ]);

        const models = data[0].data as Model[];
        const inventoryAccounts = data[1] as Account[];
        const accounts = data[2];
        const orders = flatMap(data.slice(3) as ClientResponse[], (item) => item.data) as Order[];

        return formatOrders({
            orders,
            models,
            accounts,
            inventoryAccount: inventoryAccounts[0],
        });
    }

    async getModelAndGroups(accountId: number): Promise<{
        model: ModelWithExtraInfo | null;
        groups: Group[];
    }> {
        const data = await Promise.all([this.modelApi.getModels(), this.groupApi.getGroups()]);

        const model =
            data[0].find((model) => model.accounts.map((account) => account.accountId).includes(accountId)) || null;
        const groups = data[1].filter((group) => group.accountIds.includes(accountId));

        return {
            model,
            groups,
        };
    }

    async getCorporateActions(
        accountId: number,
        dateFrom: string,
        dateTo: string,
        types?: CorporateActionsType,
    ): Promise<CorporateActionWithExtraInfo[]> {
        const positions = (await this.getPortfolio({ accountId })).entries;
        const actions = (
            await this.apis.orbis.research.corporateActions.search({
                symbol: positions.map((position) => position.symbol).join(','),
                dateFrom,
                dateTo,
                type: types,
            })
        ).data as CorporateAction[];

        return formatCorporateActions(actions, positions);
    }

    async getDriftReport(accountId: number): Promise<AccountDriftReportItem[]> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor ? this.branchApi.getAccount(accountId) : this.getAccount(accountId),
            this.getPortfolio({ accountId }),
            this.modelApi.getModels(),
        ]);

        const account = data[0];
        const positions = data[1].entries;
        const model = data[2].find((model) =>
            model.accounts.map((account) => account.accountId).includes(account?.accountId || 0),
        );

        if (!account) {
            return [];
        }

        return formatDriftReport(account, positions, model);
    }

    async getHoldingInfo(accountId: number, symbol: string, currency?: string): Promise<HoldingInfo> {
        const data = await Promise.all([
            this.config.apiPermissions.advisor ? this.branchApi.getAccount(accountId) : this.getAccount(accountId),
            this.getPortfolio({ accountId, currency }),
            this.modelApi.getModels(),
        ]);

        const account = data[0];
        const positions = data[1].entries;
        const model = data[2].find((model) =>
            model.accounts.map((account) => account.accountId).includes(account?.accountId || 0),
        );
        const modelComponent = model?.components.filter((component) => component.symbol === symbol)[0];

        return formatHoldingInfo(symbol, positions, account?.balance || 0, modelComponent?.percentage || 0);
    }

    async getAccountWithPositionInfo(symbol: string, ...accountIds: number[]): Promise<AccountWithPositionInfo[]> {
        const accounts = (await this.branchApi.getAccounts()).filter((account) =>
            accountIds.includes(account.accountId),
        );

        const accountsAvailableBalances = (
            await Promise.all(
                accounts.map((account) => {
                    if (account.multicurrencyApproach === AccountCurrencyApproach.PerCurrency) {
                        return this.apis.orbis.user.accountTradingLimit({ accountId: account.accountId });
                    } else {
                        return this.apis.orbis.user.tradingLimitInCurrency({
                            accountId: account.accountId,
                        });
                    }
                }),
            )
        ).map((item) => item.data);

        const portfolios = (
            await Promise.all(
                accountIds.map((accountId) =>
                    this.apis.orbis.user.accountPortfolioFull({
                        loadQuotes: true,
                        // @ts-expect-error: // FIXME: missing query param (loadCost). Waiting for backend update
                        loadCost: true,
                        loadIndustries: true,
                        accountId,
                    }),
                ),
            )
        ).map((item) => item.data) as Portfolio[];

        return accountIds.map((accountId, index) => {
            // we are sure that account exists

            const account = accounts.find((account) => account.accountId === accountId)!;
            const portfolio = portfolios[index]!;
            const positions = formatPortfolioPositions(portfolio.entries, [account], [], account.balance, symbol);

            return {
                ...account,
                availableCash: accountsAvailableBalances[index]!.availableCash,
                cashOnAccount: accountsAvailableBalances[index]!.cashOnAccount,
                positionValue: sumBy(positions, (position) => position.value),
                positionWeight: sumBy(positions, (position) => position.weight),
                positionShares: sumBy(positions, (position) => position.quantity),
                portfolio: {
                    ...portfolio,
                    entries: positions,
                },
            };
        });
    }

    async getAccountHistory(
        accountIds: number | number[],
        dateFrom: Date,
        dateTo: Date,
    ): Promise<AccountHistoryWithExtraInfo[]> {
        const ids = isArray(accountIds) ? accountIds : [accountIds];
        const histories = (
            await Promise.all(
                ids.map((id) =>
                    this.apis.orbis.user.accountHistory({
                        accountId: id,
                        dateFrom: formatDate(dateFrom, { pattern: API_DATE }),
                        dateTo: formatDate(dateTo, { pattern: API_DATE }),
                        // @ts-expect-error: // FIXME: missing query param (bondPriceType). Waiting for backend update
                        bondPriceType: PriceType.Percentage,
                        bondQuantityType: BondQuantityType.Unit,
                    }),
                ),
            )
        ).map((item) => {
            return item.data as AccountHistory[];
        });
        return flatMap(histories, (history) => formatAccountHistory(history));
    }

    async getAccountPerformance(
        accountId: number,
        dateFrom: string,
        withTodaysBalance?: boolean,
        currency?: string,
    ): Promise<BalanceHistory[]> {
        const account = withTodaysBalance
            ? this.config.apiPermissions.advisor
                ? await this.branchApi.getAccount(accountId)
                : await this.getAccount(accountId, currency)
            : null;

        const balanceHistory = this.config.apiPermissions.rtbs
            ? await this.apis.orbis.user.accountBalanceHistory({
                  accountId,
                  dateFrom: formatDate(dateFrom, { pattern: API_DATE }),
                  // @ts-expect-error: // FIXME: missing query param. Waiting for backend update
                  currency: currency ?? Currency.UsDollar,
              })
            : null;

        if (balanceHistory?.data) {
            if (account) {
                return (balanceHistory.data as BalanceHistory[]).concat([
                    {
                        balance: account.balance,
                        date: formatDate(new Date()),
                    },
                ]);
            } else {
                return balanceHistory.data as BalanceHistory[];
            }
        } else {
            return [];
        }
    }

    async getAccountBuyingPower(
        accountId: string | number,
        multicurrencyApproach: AccountCurrencyApproach,
        currency?: string,
    ): Promise<AccountBuyingPower> {
        const { data: tradingLimit } = await this.apis.orbis.user.accountTradingLimit({
            accountId,
            currency: currency ?? Currency.UsDollar,
        });

        let buyingPower = 0;
        let cashOnAccount = 0;

        if (multicurrencyApproach === AccountCurrencyApproach.Equivalent) {
            const tradingLimitInCurrency = (
                await this.apis.orbis.user.tradingLimitInCurrency({
                    accountId,
                    currency: currency ?? Currency.UsDollar,
                })
            ).data;
            buyingPower = tradingLimitInCurrency.availableCash;
            cashOnAccount = tradingLimitInCurrency.cashOnAccount;
        } else {
            buyingPower = tradingLimit.availableCash;
            cashOnAccount = tradingLimit.cashOnAccount;
        }

        return {
            ...tradingLimit,
            buyingPower,
            cashOnAccount,
            marginRequirement: tradingLimit.sellToOpenCash || 0,
            unsettledCash: tradingLimit.unsettledCash || 0,
            shortPositionReserve: tradingLimit.shortPositionReserve || 0,
        };
    }

    async getAccountCombinedBalance(accountId: string | number, currency?: string): Promise<AccountCombinedBalance> {
        const { data } = await this.apis.orbis.user.combinedBalancesV3({
            // @ts-expect-error: // FIXME: Work only with accountId. Waiting for backend documentation update
            accountId,
            currency,
        });
        return data;
    }

    // Plaid related
    async getTempIavKey(): Promise<string> {
        const response = await this.apis.orbis.transfers.instantAccountVerification.initiate({
            // @ts-expect-error: // FIXME: missing param. Waiting for backend update
            processorToken: this.config.apiPermissions.iavProcessorToken,
        });
        return response.data.publicKey;
    }

    // Plaid related
    async iavComplete(accountNumber: string, publicToken: string, bankAccountId: string): Promise<BankLink> {
        const data = { account: accountNumber, publicToken, bankAccountId };
        const response = await this.apis.orbis.transfers.instantAccountVerification.complete(
            this.config.apiPermissions.iavProcessorToken ? { ...data, processorToken: true } : data,
        );
        return response.data;
    }

    async getAccountTransfers(accountNumber: string): Promise<Transfer[]> {
        const response = await this.apis.orbis.transfers.transferStatus(`${accountNumber}`);
        return response.data as Transfer[];
    }

    async getAccountBankLinks(accountNumber = ''): Promise<BankLink[]> {
        const response = await this.apis.orbis.transfers.relationships.list(accountNumber);
        // Comes back as an empty obj if feature is not supported ( should throw error on back end side )
        if (isArray(response.data)) {
            return response.data;
        } else {
            throw new Error('Feature not supported');
        }
    }

    async getAccountWithdrawableCash(accountNumber: string): Promise<number> {
        const response = await this.apis.orbis.transfers.amountAvailable(`${accountNumber}`);
        return response.data?.total || 0;
    }

    async cancelBankLink(accountNumber: string, entryId: number): Promise<BankLink> {
        const response = await this.apis.orbis.transfers.relationships.cancel(`${entryId}`, {
            account: accountNumber,
            reason: 'delete',
        });
        return response.data as BankLink;
    }

    async renameBankLink(accountNumber: string, entryId: number, nickname: string): Promise<BankLink> {
        const response = await this.apis.orbis.transfers.relationships.rename(`${entryId}`, {
            // @ts-expect-error: // FIXME: missing param. Waiting for backend update
            account: accountNumber,
            nickname,
        });
        return response.data as BankLink;
    }

    async approveBankLink(values: MicroDepositFormValues): Promise<BankLink> {
        const { entryId, accountNumber, amount1, amount2 } = values;
        const response = await this.apis.orbis.transfers.relationships.approve({
            account: accountNumber,
            entryId,
            amount1,
            amount2,
        });
        return response.data as BankLink;
    }

    async createAchBankLink(values: CreateAchBankAccountFormValues): Promise<BankLink> {
        const { accountNumber, bankRoutingNumber, bankAccount, bankAccountOwnerName, bankAccountType, nickname } =
            values;

        const response = await this.apis.orbis.transfers.relationships.create({
            account: accountNumber,
            bankRoutingNumber,
            bankAccount,
            bankAccountOwnerName,
            bankAccountType,
            nickname,
        });
        return response.data as BankLink;
    }

    async createIfscBankLink(
        values: CreateIfscBankAccountFormValues & { account: string; beneficiaryName: string },
    ): Promise<BankLink> {
        const { bankAccount, ifscCode, account, beneficiaryName } = values;

        const { data } = await this.apis.orbis.getApiClient().post<BankLink>('transfer/relationship/bank-ifsc/create', {
            bankAccount,
            bankBranch: ifscCode,
            international: true,
            account,
            beneficiaryName,
        });

        return data;
    }

    async cancelIfscBankLink(id: number, account: string, reason?: string): Promise<BankLink> {
        const { data } = await this.apis.orbis
            .getApiClient()
            .post<BankLink>(`transfer/relationship/bank-ifsc/cancel/${id}`, { account, reason: reason || 'Cancel' });
        return data;
    }

    async initiateAxisBankLinkTransfer(values: AxisBankLinkFormValues & { entryId: number }): Promise<AxisBankLink> {
        const { customerPan, entryId, amount, email, phone } = values;

        const { data } = await this.apis.orbis.getApiClient().post<AxisBankLink>('transfer/bank/vtifsc/deposit', {
            customerPan,
            entryId,
            amount,
            email,
            phone,
        });
        return data;
    }

    async depositCash({
        accountNumber,
        entryId,
        amount,
        contribution,
    }: {
        accountNumber: string;
        entryId: number;
        amount: number;
        // Only necessary if the account is an IRA
        contribution?: { type: IraContributionType; priorYear: boolean };
    }): Promise<DepositWithdraw> {
        const response = await this.apis.orbis.transfers.ach.deposit({
            account: accountNumber,
            entryId,
            amount,
            contribution,
        });
        return response.data;
    }

    async withdrawCash({
        accountNumber,
        entryId,
        amount,
        distribution,
    }: {
        accountNumber: string;
        entryId: number;
        amount: number;
        // Only necessary if the account is an IRA
        distribution?: {
            institution?: string;
            reason?: IraDistributionReasonType;
            federalTax?: {
                type: IraDistributionTaxType;
                amount: number;
            };
            stateTax?: {
                type: IraDistributionTaxType;
                amount: number;
            };
        };
    }): Promise<DepositWithdraw> {
        const response = await this.apis.orbis.transfers.ach.withdraw({
            account: accountNumber,
            entryId,
            amount,
            distribution,
        });
        return response.data;
    }

    async requestCheckWithdrawal(values: RequestCheckWithdrawalFormValues): Promise<DepositWithdraw> {
        const {
            accountNumber,
            amount,
            deliveryMethod,
            thirdParty,
            name,
            streetAddress,
            country,
            city,
            postalCode,
            stateProvince,
        } = values;

        const response = await this.apis.orbis.transfers.cheques.withdrawal({
            account: accountNumber,
            amount,
            deliveryMethod,
            beneficiary: {
                thirdParty,
                name,
                address: {
                    // Removing line breaks from streetAddress
                    streetAddress: [streetAddress.replace(/\r?\n/g, ' ')],
                    country,
                    city,
                    postalCode,
                    stateProvince,
                },
            },
        });
        return response.data;
    }

    async cancelTransaction(accountNumber: string, transferId: string): Promise<CancelTransaction> {
        const response = await this.apis.orbis.transfers.cancel({ account: accountNumber, transferId });
        return response.data;
    }

    async applyAccountCharges(accountId: number, amount: number): Promise<AccountChargesApply> {
        const formattedDate = formatDate(new Date(), { withTime: true, pattern: API_DATE_WITH_TIME_AND_TIMEZONE });
        const response = await this.apis.orbis.advisory.charges.apply({
            accountId,
            date: formattedDate,
            amount,
        });
        return response.data;
    }
    async cancelAccountCharges(chargeRef: string): Promise<AccountChargesCancel> {
        const response = await this.apis.orbis.advisory.charges.cancel({ chargeRef });
        return response.data;
    }

    async getAccountCharges(
        accountId: number,
        dateFrom?: Date,
        dateTo?: Date,
        chargeRef?: string,
    ): Promise<AccountCharge[]> {
        const from = dateFrom ? formatDate(dateFrom, { withTime: true }) : undefined;
        const to = dateTo ? formatDate(dateTo, { withTime: true }) : undefined;

        const response = await this.apis.orbis.advisory.charges.search({
            AccountId: accountId,
            DateFrom: from,
            DateTo: to,
            ChargeRef: chargeRef,
        });
        return response.data;
    }

    async getAcatTransfer(accountId: number): Promise<Acat[]> {
        const response = await this.apis.orbis.transfers.acats.search({ account: { accountId } });
        return response.data;
    }

    async initiateAcatTransfer({
        accountNumber,
        type,
        contraAccount,
        contraAccountType,
        contraTitle,
        primaryTaxId,
        secondaryTaxId,
        participantNumber,
        comment,
        assets,
    }: {
        accountNumber: string;
        type: AcatTransferType;
        contraAccount: string;
        contraAccountType: AcatAccountContraType;
        contraTitle: string;
        primaryTaxId: string;
        secondaryTaxId?: string;
        participantNumber: string;
        comment?: string;
        assets?: { symbol: string; quantity: number }[];
    }) {
        const response = await this.apis.orbis.transfers.acats.initiateSchedule({
            account: { accountNumber },
            type,
            contraAccount,
            contraTitle,
            contraAccountType,
            primaryTaxId,
            secondaryTaxId,
            participantNumber,
            comment,
            assets,
        });
        return response.data;
    }

    async cancelAcatTransfer(requestId: string) {
        const response = await this.apis.orbis.transfers.acats.cancel({ requestId });
        return response.data;
    }

    async wireTransfer(values: WireTransferFormValues, selectedAccount: BankLink) {
        const { amount } = values;
        const { entryId, account, international, beneficiary, recipientBank } = selectedAccount;
        const response = await this.apis.orbis.transfers.wires.outgoing({
            disbursementType: WireDisbursementType.Partial,
            amount,
            entryId,
            account,
            international,
            beneficiary,
            recipientBank,
        });
        return response.data;
    }
}
